import { Regions } from '../constants/Regions'
import alpha from './settings.alpha.json'
import prod from './settings.prod.json'

interface AppConfigSettings {
  cognitoIdentityProviderId: string
  allowedUploadExtensions: string
  cognitoUserInfoEndpoint: string
  cognitoRevokeEndpoint: string
  cognitoTokenEndpoint: string
  cognitoIdentityPoolId: string
  azureSearchIndexName: string
  azureSearchEndpoint: string
  parseServerEndpoint: string
  parseServerAppId: string
  cognitoClientId: string
  templateBucket: string
  devlopmentUrl: string
  cloudFrontUrl: string
  clientBucket: string
  casesBucket: string
  restApiUrl: string
  sesRoleArn: string
  loginUrl: string
  apiKey: string
}

export class AppConfig {
  private static _instance: AppConfig
  private readonly appConfig: AppConfigSettings

  constructor() {
    const isProd = this.isProduction()

    this.appConfig = {
      apiKey: isProd ? prod.apiKey : alpha.apiKey,
      loginUrl: isProd ? prod.loginUrl : alpha.loginUrl,
      sesRoleArn: isProd ? prod.sesRoleArn : alpha.sesRoleArn,
      restApiUrl: isProd ? prod.restApiUrl : alpha.restApiUrl,
      casesBucket: isProd ? prod.casesBucket : alpha.casesBucket,
      devlopmentUrl: isProd ? prod.devlopmentUrl : alpha.devlopmentUrl,
      cloudFrontUrl: isProd ? prod.cloudFrontUrl : alpha.cloudFrontUrl,
      templateBucket: isProd ? prod.templateBucket : alpha.templateBucket,
      cognitoClientId: isProd ? prod.cognitoClientId : alpha.cognitoClientId,
      parseServerAppId: isProd ? prod.parseServerAppId : alpha.parseServerAppId,
      parseServerEndpoint: isProd ? prod.parseServerEndpoint : alpha.parseServerEndpoint,
      azureSearchEndpoint: isProd ? prod.azureSearchEndpoint : alpha.azureSearchEndpoint,
      azureSearchIndexName: isProd ? prod.azureSearchIndexName : alpha.azureSearchIndexName,
      cognitoTokenEndpoint: isProd ? prod.cognitoTokenEndpoint : alpha.cognitoTokenEndpoint,
      cognitoIdentityPoolId: isProd ? prod.cognitoIdentityPoolId : alpha.cognitoIdentityPoolId,
      cognitoRevokeEndpoint: isProd ? prod.cognitoRevokeEndpoint : alpha.cognitoRevokeEndpoint,
      allowedUploadExtensions: isProd ? prod.allowedUploadExtensions : alpha.allowedUploadExtensions,
      cognitoUserInfoEndpoint: isProd ? prod.cognitoUserInfoEndpoint : alpha.cognitoUserInfoEndpoint,
      cognitoIdentityProviderId: isProd ? prod.cognitoIdentityProviderId : alpha.cognitoIdentityProviderId,
    } as AppConfigSettings
  }

  static getInstance() {
    return this._instance || (this._instance = new this())
  }

  isLocal(): boolean {
    return window.location.origin.startsWith('http://localhost')
  }

  isAlpha(): boolean {
    return window.location.origin.startsWith('https://alpha.gaylordcases.com')
  }

  isProduction(): boolean {
    return window.location.origin.startsWith('https://app.gaylordcases.com')
  }

  getBaseUrl(): string {
    if (this.isLocal()) {
      return 'https://alpha.gaylordcases.com'
    }

    return window.location.origin
  }

  getStage(): string {
    if (this.isLocal()) {
      return 'Local'
    } else if (this.isAlpha()) {
      return 'Alpha'
    } else if (this.isProduction()) {
      return 'Prod'
    }

    throw new Error('An unknown stage has been detected in AppConfig.getStage().')
  }

  getRegion(): string {
    return Regions.IAD_REGION;
  }

  getClientBucket(): string {
    return this.appConfig.clientBucket;
  }

  getCasesBucket(): string {
    return this.appConfig.casesBucket;
  }

  getTemplateBucket(): string {
    return this.appConfig.templateBucket;
  }

  getParseServerEndpoint(): string {
    return this.appConfig.parseServerEndpoint
  }

  getCognitoUserInfoEndpoint(): string {
    return this.appConfig.cognitoUserInfoEndpoint
  }

  getCognitoRevokeEndpoint(): string {
    return this.appConfig.cognitoRevokeEndpoint
  }

  getCognitoTokenEndpoint(): string {
    return this.appConfig.cognitoTokenEndpoint
  }

  getCognitoClientId(): string {
    return this.appConfig.cognitoClientId
  }

  getCognitoIdentityPoolId(): string {
    return this.appConfig.cognitoIdentityPoolId
  }

  getCognitoIdentityProviderId(): string {
    return this.appConfig.cognitoIdentityProviderId
  }

  getUploadExtensions(): string[] {
    return this.appConfig.allowedUploadExtensions.split(',').map(region => region?.trim())
  }

  getRedirectUrl(): string {
    if (this.isLocal()) {
      return this.getDevlopmentUrl()
    }

    if (this.isAlpha() || this.isProduction()) {
      return this.getCloudFrontUrl()
    }

    throw new Error('Detected an unknown stage/environment, please reconfigure the project.')
  }

  getLoginUrl(): string {
    const redirectUrl = this.getRedirectUrl()
    return encodeURI(this.appConfig.loginUrl + redirectUrl)
  }

  getBack4AppClassesUrl(typeName?: string): string {
    const baseUrl = `${this.getParseServerEndpoint()}/classes/`
    return typeName ? baseUrl + typeName : baseUrl
  }

  getRestApiUrl(): string {
    return this.appConfig.restApiUrl
  }

  getBack4AppAppId(): string {
    return this.appConfig.parseServerAppId
  }

  getAzureSearchIndexName(): string {
    return this.appConfig.azureSearchIndexName
  }

  getAzureSearchEndpoint(): string {
    return this.appConfig.azureSearchEndpoint
  }

  getSesRoleArn(): string {
    return this.appConfig.sesRoleArn
  }

  getApiKey(): string {
    return this.appConfig.apiKey
  }

  private getDevlopmentUrl(): string {
    return this.appConfig.devlopmentUrl
  }

  private getCloudFrontUrl(): string {
    return this.appConfig.cloudFrontUrl
  }
}

export default AppConfig

export const APP_CONFIG = AppConfig.getInstance()
